import { useCallback, useEffect, useRef, useState } from "react";

import { useTwoFactorAction } from "@/shared/hooks/useTwoFactorAction";
import { readApiCode, readApiStatus } from "@/shared/utils/apiError";

import { VerificationService } from "../services/verificationService";
import type {
  KycApplicantLinkPreview,
  KycApplicantLinkRequest,
  KycApplicantLinkResult,
} from "../types/verification-api.types";

/** Why a preview or a link failed, as the drawer words it. */
export type KycApplicantLinkFailure =
  /** A refusal the backend coded (a blocker, `link_stale`, `identity_required`…): render `code`. */
  { kind: "refused"; code: string } | { kind: "provider_unavailable" } | { kind: "generic" };

export function kycApplicantLinkFailure(error: unknown): KycApplicantLinkFailure {
  const code = readApiCode(error);
  if (code) return { kind: "refused", code };
  if (readApiStatus(error) === 502) return { kind: "provider_unavailable" };
  return { kind: "generic" };
}

/**
 * Staff link of a KYC applicant approved outside the platform to one organization: the read-only preview
 * (what the link would do, what refuses it, the identity pre-filled from the applicant) and the link itself
 * behind the caller's TOTP. The backend re-checks everything on the link, so the preview only guides.
 */
export function useKycApplicantLink(organizationId: string) {
  const [preview, setPreview] = useState<KycApplicantLinkPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewFailure, setPreviewFailure] = useState<KycApplicantLinkFailure | null>(null);
  const [linkFailure, setLinkFailure] = useState<KycApplicantLinkFailure | null>(null);
  // Last request wins: an answer for an earlier search is dropped.
  const requestRef = useRef(0);
  // The challenge closes as soon as the code leaves; the drawer shows its own progress while `isRunning`.
  const twoFactor = useTwoFactorAction<KycApplicantLinkResult>({ closeOnSend: true });

  const reset = useCallback(() => {
    requestRef.current += 1;
    setPreview(null);
    setPreviewing(false);
    setPreviewFailure(null);
    setLinkFailure(null);
  }, []);

  // Another organization is another link.
  useEffect(() => {
    reset();
  }, [organizationId, reset]);

  const search = useCallback(
    async (applicantId: string) => {
      const request = ++requestRef.current;
      setPreviewing(true);
      setPreviewFailure(null);
      setLinkFailure(null);
      try {
        const { data } = await VerificationService.previewKycApplicantLink(organizationId, applicantId);
        if (requestRef.current !== request) return;
        setPreview(data);
      } catch (error) {
        if (requestRef.current !== request) return;
        setPreview(null);
        setPreviewFailure(kycApplicantLinkFailure(error));
      } finally {
        if (requestRef.current === request) setPreviewing(false);
      }
    },
    [organizationId],
  );

  /** Resolves to the result, or `null` when the challenge was cancelled or the link refused. */
  const link = useCallback(
    async (
      request: Omit<KycApplicantLinkRequest, "organization_id">,
    ): Promise<KycApplicantLinkResult | null> => {
      setLinkFailure(null);
      try {
        return await twoFactor.start(async (code) => {
          const { data } = await VerificationService.linkKycApplicant(
            { ...request, organization_id: organizationId },
            code,
          );
          return data;
        });
      } catch (error) {
        setLinkFailure(kycApplicantLinkFailure(error));
        return null;
      }
    },
    [organizationId, twoFactor],
  );

  return {
    preview,
    previewing,
    previewFailure,
    search,
    reset,
    link,
    linking: twoFactor.isRunning,
    linkFailure,
    twoFactor,
  };
}
